import { useContext, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@nucleus-commerce/ui/components/sheet';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { useIsDesktop } from '../hooks/use-is-desktop';

import { DocsRailTargetContext } from './docs-shell-context';

export interface DocsRailProps {
  /** Heading of the rail; also the floating button and sheet title below `lg`. */
  label: string;
  /** Screen-reader description of the sheet below `lg`. */
  description?: string;
  /** `toc` is a narrow column for "On this page"; `panel` is wide enough for the try-it form. */
  size?: 'toc' | 'panel';
  children: ReactNode;
}

/**
 * Puts its children in the shell's right rail at `lg` and up, and behind a floating button that
 * opens a sheet below it. Only one of the two is mounted, so stateful panels never run twice.
 * A page that renders no DocsRail gets no rail column.
 */
export function DocsRail({ label, description, size = 'toc', children }: DocsRailProps) {
  const target = useContext(DocsRailTargetContext);
  const desktop = useIsDesktop();
  const [open, setOpen] = useState(false);

  if (desktop) {
    if (!target) return null;
    return createPortal(
      <section
        aria-label={label}
        className={cn('flex flex-col gap-3 p-6', size === 'panel' ? 'w-[28rem]' : 'w-60')}
      >
        {size === 'toc' ? <h2 className="text-foreground text-sm font-medium">{label}</h2> : null}
        {children}
      </section>,
      target,
    );
  }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button size="sm" className="fixed right-4 bottom-4 z-30 shadow-lg">
          {label}
        </Button>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="w-full overflow-y-auto sm:max-w-md"
        {...(description ? {} : { 'aria-describedby': undefined })}
      >
        <SheetHeader>
          <SheetTitle>{label}</SheetTitle>
          {description ? <SheetDescription>{description}</SheetDescription> : null}
        </SheetHeader>
        <div className="px-4 pb-6">{children}</div>
      </SheetContent>
    </Sheet>
  );
}
